import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicNavbar from '../components/PublicNavbar';
import LoginModal from '../components/LoginModal';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './AboutUs.css';
import './Pricing.css';

function Pricing() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { user } = useAuth();
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {user ? (
        <Navbar />
      ) : (
        <PublicNavbar onSignInClick={() => setIsModalOpen(true)} />
      )}

      <div className="pricing-container">
        <div className="pricing-content">
          {/* Hero Section */}
          <section className="pricing-hero-section">
            <div className="hero-content">
              <h1 className="hero-title">Simple, Transparent Pricing</h1>
              <p className="hero-subtitle">
                Choose the plan that fits your needs. Upgrade or downgrade
                anytime.
              </p>
            </div>
          </section>

          {/* Billing Cycle Toggle */}
          <div className="billing-cycle-toggle">
            <button
              className={`cycle-btn ${selectedBillingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setSelectedBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`cycle-btn ${selectedBillingCycle === 'yearly' ? 'active' : ''}`}
              onClick={() => setSelectedBillingCycle('yearly')}
            >
              Yearly
              <span className="save-badge-toggle">Save up to $80</span>
            </button>
          </div>

          {/* Comparison Matrix */}
          <div className="comparison-wrapper">
            <div className="comparison-table">
              {/* Header Row */}
              <div className="comparison-row header-row">
                <div className="comparison-cell feature-label"></div>

                {/* Free Plan */}
                <div className="comparison-cell plan-header">
                  <div className="plan-name">Free</div>
                  <div className="plan-price">$0</div>
                  <div className="plan-period">forever</div>
                </div>

                {/* Pro Plan */}
                <div className="comparison-cell plan-header popular-plan">
                  <div className="popular-flag">Most Popular</div>
                  <div className="plan-name">Pro</div>
                  <div className="plan-price">
                    {selectedBillingCycle === 'monthly' ? '$7.99' : '$6.58'}
                  </div>
                  <div className="plan-period">
                    {selectedBillingCycle === 'monthly'
                      ? '/month'
                      : '/month (billed yearly)'}
                  </div>
                </div>

                {/* Premium Plan */}
                <div className="comparison-cell plan-header">
                  <div className="plan-name">Premium</div>
                  <div className="plan-price">
                    {selectedBillingCycle === 'monthly' ? '$15.99' : '$13.25'}
                  </div>
                  <div className="plan-period">
                    {selectedBillingCycle === 'monthly'
                      ? '/month'
                      : '/month (billed yearly)'}
                  </div>
                </div>

                {/* Organization Plan */}
                <div className="comparison-cell plan-header org-plan">
                  <div className="org-flag">For Leagues</div>
                  <div className="plan-name">Organization</div>
                  <div className="plan-price">
                    {selectedBillingCycle === 'monthly' ? '$39.99' : '$33.25'}
                  </div>
                  <div className="plan-period">
                    {selectedBillingCycle === 'monthly'
                      ? '/month'
                      : '/month (billed yearly)'}
                  </div>
                </div>
              </div>

              {/* ── GRAPHICS SECTION ── */}
              <div className="comparison-row section-group-header">
                <div className="comparison-cell feature-label">Graphics</div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Teams</div>
                <div className="comparison-cell">1</div>
                <div className="comparison-cell">5</div>
                <div className="comparison-cell">Unlimited</div>
                <div className="comparison-cell">Unlimited</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Players</div>
                <div className="comparison-cell">Unlimited</div>
                <div className="comparison-cell">Unlimited</div>
                <div className="comparison-cell">Unlimited</div>
                <div className="comparison-cell">Unlimited</div>
              </div>
              <div className="comparison-row">
                <div className="comparison-cell feature-label">Saved Graphics</div>
                <div className="comparison-cell">10</div>
                <div className="comparison-cell">50</div>
                <div className="comparison-cell">Unlimited</div>
                <div className="comparison-cell">Unlimited</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">All Templates & Designs</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Custom Team Colors</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Logo Upload</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Watermark Removed</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Background Removal & Cutouts</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Roster Management</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>
              <div className="comparison-row">
                <div className="comparison-cell feature-label">Animated Graphics (GIF)</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell coming-soon-cell">Coming Soon</div>
                <div className="comparison-cell coming-soon-cell">Coming Soon</div>
                <div className="comparison-cell coming-soon-cell">Coming Soon</div>
              </div>
              <div className="comparison-row">
                <div className="comparison-cell feature-label">Video Highlights w/ Stats</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell coming-soon-cell">Coming Soon</div>
                <div className="comparison-cell coming-soon-cell">Coming Soon</div>
              </div>

              {/* ── ANALYTICS SECTION ── */}
              <div className="comparison-row section-group-header">
                <div className="comparison-cell feature-label">Player Analytics</div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Stat Tracking & Game History</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Brag Cards</div>
                <div className="comparison-cell">10</div>
                <div className="comparison-cell">50</div>
                <div className="comparison-cell">Unlimited</div>
                <div className="comparison-cell">Unlimited</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Benchmark Comparisons by Level</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Development Insights</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Ask the Analyst</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">20 messages/mo</div>
                <div className="comparison-cell">200 messages/mo</div>
                <div className="comparison-cell">200 messages/mo</div>
              </div>

              {/* ── ORGANIZATION SECTION ── */}
              <div className="comparison-row section-group-header">
                <div className="comparison-cell feature-label">Organization</div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
                <div className="comparison-cell"></div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Organization Team Management</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Invite Coaches to Teams</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Bulk Team CSV Import</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Control Template & Feature Restrictions</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
              </div>

              <div className="comparison-row">
                <div className="comparison-cell feature-label">Control Team Logo & Color Defaults</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✗</div>
                <div className="comparison-cell">✓</div>
              </div>

              {/* CTA Row */}
              <div className="cta-row-full">
                <button
                  className="btn-signup-full"
                  onClick={() => navigate(user ? '/membership' : '/register')}
                >
                  {user ? 'View Your Membership' : 'Sign Up for Free'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <LoginModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}

export default Pricing;
